import React, { useCallback, useEffect, useState } from 'react';
import { CircleAlert } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import useUser from '../../../hooks/useUser';
import { getErrorMessage } from '../../../utils/errorMessage';
import { Alert, Button } from '../../../design-system';
import AccountSettingsLoadingState from '../AccountSettingsLoadingState';
import UpgradeOfferSummary, {
  type UpgradeOfferPlan,
} from '../../../components/UpgradeOfferSummary';
import { navigateToCheckoutOverlay } from '../../../utils/checkoutOverlayNavigation';
import {
  getAccountSettingsUserCacheKey,
  makeAccountSettingsCacheKey,
  readAccountSettingsCache,
  writeAccountSettingsCache,
} from '../accountSettingsDataCache';
import './UsageSettings.css';

type ModelUsageRow = {
  model: string;
  events: number;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  uncachedInputTokens: number;
  cacheWriteInputTokens: number;
  estimatedCostUsd: number;
};

type UsageSummaryResponse = {
  cycleStartDate: string | null;
  cycleEndDate: string | null;
  limits: {
    maxMessagesPerMonth: number;
    maxStrategyRevisionsPerMonth: number;
    maxBacktestsPerMonth: number;
    maxBacktestMinutesPerMonth: number;
    maxBots: number;
  };
  upgrade?: {
    currentTier?: { productName: string };
    resetDate?: string;
    productName: string | null;
    checkoutUrl: string | null;
    contactSalesUrl?: string | null;
    monthlyAiRetailCreditUsd?: string | null;
    plans?: UpgradeOfferPlan[];
    terminal?: boolean;
  } | null;
  usage: {
    messagesUsed: number;
    messagesRemaining: number;
    revisionsUsed: number;
    revisionsRemaining: number;
    backtestsUsed: number;
    backtestMinutesUsed: number;
    backtestMinutesRemaining: number | null;
    deploymentRuntimeMinutesUsed: number;
    ai: {
      eventCount: number;
      inputTokens: number;
      outputTokens: number;
      cachedInputTokens: number;
      uncachedInputTokens: number;
      cacheWriteInputTokens: number;
      estimatedCostUsd: number;
      byModel: ModelUsageRow[];
      allowanceNanoUsd: string;
      usedNanoUsd: string;
      remainingNanoUsd: string;
      permanentRemainingNanoUsd: string;
      percentageUsed: number;
      displayMode: 'percentage_only' | 'percentage_and_retail_debug';
      display: {
        allowanceUsd: string;
        usedUsd: string;
        remainingUsd: string;
        permanentRemainingUsd: string;
      };
      bySourceNanoUsd: {
        agent: string;
        strategy: string;
      };
    };
  };
  message?: string;
};

const formatNumber = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return 'n/a';
  return new Intl.NumberFormat('en-US').format(value);
};

const formatUsd = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return 'n/a';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
};

const formatRetailUsd = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return 'n/a';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatLimit = (value: number | null | undefined): string => {
  if (value == null) return 'n/a';
  if (value < 0) return 'n/a';
  return formatNumber(value);
};

const nanoUsdToNumber = (value: string | null | undefined): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed / 1_000_000_000 : 0;
};

const clampPercentage = (value: number | null | undefined): number => {
  if (value == null || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
};

const formatDate = (iso: string | null): string => {
  if (!iso) return 'n/a';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'n/a';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

const UsageSettings = (): React.ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getApiToken, isAuthenticated, authLoading, user } = useUser();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'api';
  const cacheKey = makeAccountSettingsCacheKey(
    'usage-summary',
    getAccountSettingsUserCacheKey(user),
    apiBaseUrl,
  );
  const cached = readAccountSettingsCache<UsageSummaryResponse>(cacheKey);
  const [summary, setSummary] = useState<UsageSummaryResponse | null>(() => cached || null);
  const [loading, setLoading] = useState(() => !cached);
  const [error, setError] = useState('');

  const loadUsage = useCallback(
    async (options: { force?: boolean } = {}) => {
      if (!isAuthenticated || authLoading) return;
      if (!options.force) {
        const existing = readAccountSettingsCache<UsageSummaryResponse>(cacheKey);
        if (existing) {
          setSummary(existing);
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      setError('');
      try {
        const token = await getApiToken();
        const response = await fetch(`${apiBaseUrl}/agent/usage-summary`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        // SAFETY: The authenticated endpoint is validated immediately through the fields read below.
        const data = (await response.json()) as UsageSummaryResponse;
        if (!response.ok) {
          throw new Error(data.message || 'Could not load usage summary.');
        }
        writeAccountSettingsCache(cacheKey, data);
        setSummary(data);
      } catch (err) {
        setError(getErrorMessage(err, 'Could not load usage summary.'));
      } finally {
        setLoading(false);
      }
    },
    [apiBaseUrl, authLoading, cacheKey, getApiToken, isAuthenticated],
  );

  useEffect(() => {
    void loadUsage();
  }, [loadUsage]);

  if (authLoading || (loading && !summary)) {
    return <AccountSettingsLoadingState label="Usage" text="Loading this billing cycle." />;
  }

  const usage = summary?.usage;
  const limits = summary?.limits;
  const ai = usage?.ai;
  const aiPercentage = clampPercentage(ai?.percentageUsed);
  const aiAllowanceNanoUsd = nanoUsdToNumber(ai?.allowanceNanoUsd);
  const hasAiAllowance = aiAllowanceNanoUsd > 0;
  const showRetailDebug = ai?.displayMode === 'percentage_and_retail_debug';
  const sourceDisplay = (sourceNanoUsd: string | null | undefined): string => {
    const sourceUsed = nanoUsdToNumber(sourceNanoUsd);
    return formatRetailUsd(sourceUsed);
  };
  const aiRemainingUsd = Number(ai?.display?.remainingUsd);
  const aiAllowanceUsd = Number(ai?.display?.allowanceUsd);
  const permanentRemainingUsd = Number(ai?.display?.permanentRemainingUsd);
  const isAiExhausted = hasAiAllowance && aiPercentage >= 100;
  const shouldWarnAi = hasAiAllowance && aiPercentage >= 80 && !isAiExhausted;
  const backtestPercentage =
    limits?.maxBacktestMinutesPerMonth && limits.maxBacktestMinutesPerMonth > 0
      ? clampPercentage(
          ((usage?.backtestMinutesUsed ?? 0) / limits.maxBacktestMinutesPerMonth) * 100,
        )
      : 0;

  return (
    <div className="usage-settings" data-testid="usage-settings">
      <div className="settings-subpage-header">
        <div className="settings-subpage-header-left">
          <h2 className="settings-subpage-title">Usage</h2>
          <p className="settings-subpage-description">
            This billing cycle. Resets {formatDate(summary?.cycleEndDate ?? null)}.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => {
            void loadUsage({ force: true });
          }}
        >
          Refresh
        </Button>
      </div>

      {error ? (
        <Alert variant="danger" className="usage-settings-error">
          {error}
        </Alert>
      ) : null}

      {shouldWarnAi ? (
        <Alert variant="warning" className="usage-settings-warning">
          {Math.round(aiPercentage)}% of monthly AI credits used. Credits reset{' '}
          {formatDate(summary?.cycleEndDate ?? null)}.
        </Alert>
      ) : null}
      <section
        className={`usage-credit-card${isAiExhausted ? ' usage-credit-card--exhausted' : ''}${shouldWarnAi ? ' usage-credit-card--warning' : ''}`}
        aria-label="AI credit usage"
      >
        <div className="usage-credit-heading">
          <div>
            <span className="usage-metric-label">
              {isAiExhausted && summary?.upgrade?.currentTier?.productName
                ? `Current plan: ${summary.upgrade.currentTier.productName}`
                : hasAiAllowance
                  ? 'AI credits'
                  : 'AI activity'}
            </span>
            <strong className="usage-credit-percent">
              {isAiExhausted ? (
                <>
                  <CircleAlert size={24} aria-hidden="true" />
                  You’re out of AI credits
                </>
              ) : hasAiAllowance ? (
                `${Math.round(aiPercentage)}% of credits used`
              ) : (
                `${formatNumber(ai?.eventCount)} AI calls`
              )}
            </strong>
            {isAiExhausted ? (
              <span className="usage-credit-balance">
                100% used · Resets {formatDate(summary?.cycleEndDate ?? null)}
              </span>
            ) : hasAiAllowance ? (
              <span className="usage-credit-balance">
                {formatRetailUsd(aiRemainingUsd)} remaining of {formatRetailUsd(aiAllowanceUsd)}
              </span>
            ) : null}
          </div>
          {!hasAiAllowance && (ai?.eventCount ?? 0) > 0 ? (
            <span className="usage-credit-debug">
              {formatUsd(ai?.estimatedCostUsd)} provider estimate
            </span>
          ) : null}
        </div>
        {hasAiAllowance ? (
          <>
            <div
              className="usage-progress"
              role="progressbar"
              aria-label="Monthly AI credits used"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(aiPercentage)}
            >
              <span style={{ width: `${aiPercentage}%` }} />
            </div>
          </>
        ) : (
          <p className="usage-empty">
            No monthly credit allowance is attached to this entitlement. Recorded calls are still
            shown here.
          </p>
        )}
        {summary?.upgrade &&
        (!summary.upgrade.terminal || isAiExhausted || backtestPercentage >= 100) ? (
          <div className="usage-upgrade-offer">
            <UpgradeOfferSummary
              compact
              hideCurrentPlan={isAiExhausted}
              currentAiAllowanceUsd={aiAllowanceNanoUsd}
              offer={{
                ...summary.upgrade,
                resetDate: summary.upgrade.resetDate ?? summary.cycleEndDate,
              }}
              reason={
                summary.upgrade.terminal
                  ? 'You’ve reached your included allowance.'
                  : backtestPercentage >= 100
                    ? 'You’re out of backtest time.'
                    : undefined
              }
              onUpgrade={() =>
                summary.upgrade?.checkoutUrl &&
                navigateToCheckoutOverlay(navigate, summary.upgrade.checkoutUrl, location)
              }
            />
            {isAiExhausted && !summary.upgrade.terminal && (
              <p className="usage-reset-alternative">You can also wait for the reset.</p>
            )}
          </div>
        ) : summary?.upgrade?.terminal ? (
          <p className="usage-cycle-note">
            Current plan: {summary.upgrade.currentTier?.productName || 'your highest plan'}. You’re
            on our highest plan. No upgrade is needed.
          </p>
        ) : null}
        {hasAiAllowance && (
          <details className="usage-breakdown">
            <summary>Usage breakdown</summary>
            {isAiExhausted && (
              <p className="usage-credit-balance">
                {formatRetailUsd(aiRemainingUsd)} remaining of {formatRetailUsd(aiAllowanceUsd)}
              </p>
            )}
            {showRetailDebug && (
              <p className="usage-credit-debug">
                {formatUsd(Number(ai?.display?.usedUsd))} of{' '}
                {formatUsd(Number(ai?.display?.allowanceUsd))} · {formatNumber(ai?.eventCount)}{' '}
                tracked calls
              </p>
            )}
            <div className="usage-source-grid">
              <div>
                <span>AI Agent</span>
                <strong>{sourceDisplay(ai?.bySourceNanoUsd?.agent)}</strong>
              </div>
              <div>
                <span>Strategies</span>
                <strong>{sourceDisplay(ai?.bySourceNanoUsd?.strategy)}</strong>
              </div>
              <div>
                <span>Extra credits</span>
                <strong>
                  {permanentRemainingUsd > 0
                    ? `${formatRetailUsd(permanentRemainingUsd)} extra credits`
                    : 'None'}
                </strong>
              </div>
            </div>
          </details>
        )}
      </section>

      <section className="usage-credit-card" aria-label="Backtest usage">
        <div className="usage-credit-heading">
          <div>
            <span className="usage-metric-label">Backtest minutes</span>
            <strong className="usage-credit-percent">
              {formatNumber(usage?.backtestMinutesUsed)} /{' '}
              {formatLimit(limits?.maxBacktestMinutesPerMonth)}
            </strong>
          </div>
          <span className="usage-credit-debug">{Math.round(backtestPercentage)}% used</span>
        </div>
        <div
          className="usage-progress usage-progress-backtest"
          role="progressbar"
          aria-label="Monthly backtest minutes used"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(backtestPercentage)}
        >
          <span style={{ width: `${backtestPercentage}%` }} />
        </div>
      </section>

      <section className="usage-credit-card" aria-label="Live bot runtime usage">
        <div className="usage-credit-heading">
          <div>
            <span className="usage-metric-label">Live bot runtime</span>
            <strong className="usage-credit-percent">
              {formatNumber(usage?.deploymentRuntimeMinutesUsed)} minutes
            </strong>
            <span className="usage-credit-balance">
              Measured only. No runtime limit is applied.
            </span>
          </div>
        </div>
      </section>

      <details className="usage-technical-details">
        <summary>Technical details</summary>
        <dl className="usage-token-list">
          <div>
            <dt>Input tokens</dt>
            <dd>{formatNumber(ai?.inputTokens)}</dd>
          </div>
          <div>
            <dt>Output tokens</dt>
            <dd>{formatNumber(ai?.outputTokens)}</dd>
          </div>
          <div>
            <dt>Provider calls</dt>
            <dd>{formatNumber(ai?.eventCount)}</dd>
          </div>
        </dl>
        <h3 className="usage-section-title">By model</h3>
        {!ai?.byModel?.length ? (
          <p className="usage-empty">No AI usage recorded in this cycle yet.</p>
        ) : (
          <div className="usage-model-table-wrap">
            <table className="usage-model-table">
              <thead>
                <tr>
                  <th scope="col">Model</th>
                  <th scope="col">Events</th>
                  <th scope="col">Input</th>
                  <th scope="col">Output</th>
                  <th scope="col">Cached</th>
                  <th scope="col">Provider est.</th>
                </tr>
              </thead>
              <tbody>
                {ai.byModel.map((row) => (
                  <tr key={row.model}>
                    <td>{row.model}</td>
                    <td>{formatNumber(row.events)}</td>
                    <td>{formatNumber(row.inputTokens)}</td>
                    <td>{formatNumber(row.outputTokens)}</td>
                    <td>{formatNumber(row.cachedInputTokens)}</td>
                    <td>{formatUsd(row.estimatedCostUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </details>

      <p className="usage-cycle-note">
        Cycle {formatDate(summary?.cycleStartDate ?? null)} to{' '}
        {formatDate(summary?.cycleEndDate ?? null)}. AI credits include BotSpot managed Agent and
        strategy calls. BYOK calls are not charged.
      </p>
    </div>
  );
};

export default UsageSettings;
